/**
 * @fileoverview Pure utility functions for fetch operations with retry logic.
 *
 * This module provides reusable, testable fetch utilities with:
 * - Exponential backoff with configurable jitter
 * - Timeout handling via AbortController
 * - Transient error detection for smart retry decisions
 * - Configurable retry parameters
 *
 * Design Principles:
 * - No React dependencies - pure functions that are easily testable
 * - No module-level state - all state passed as parameters
 * - AbortSignal-based cancellation for proper cleanup
 * - Comprehensive error handling with typed errors
 *
 * @example
 * ```tsx
 * // Basic usage with defaults
 * const controller = new AbortController();
 * const data = await fetchWithRetry<MyData>('/api/data', controller.signal, 'data-fetch');
 *
 * // Custom retry configuration
 * const data = await fetchWithRetry<MyData>(
 *   '/api/data',
 *   controller.signal,
 *   'data-fetch',
 *   { maxAttempts: 5, timeoutMs: 60000 }
 * );
 *
 * // Cleanup on component unmount
 * useEffect(() => {
 *   return () => controller.abort();
 * }, []);
 * ```
 *
 * @see react-ui/src/hooks/bootstrap/types.ts for RetryConfig type
 * @see react-ui/src/services/apiErrors.ts for error types
 * @see docs/adr/0002-bootstrap-state-hydration.md for bootstrap architecture
 */

import { api } from '../../services/api';
import { BootstrapTimeoutError, RetryExhaustedError } from '../../services/apiErrors';
import { logger } from '../../services/logger.client';
import { type RetryConfig, DEFAULT_RETRY_CONFIG, isTransientError } from './types';

// =============================================================================
// Exported Constants
// =============================================================================

/**
 * Default timeout for fetch requests in milliseconds.
 *
 * 30 seconds is generous enough for slow connections while preventing
 * indefinite hangs. This value is used when no custom timeout is specified.
 */
export const DEFAULT_TIMEOUT_MS = 30000;

/**
 * Default maximum number of retry attempts for transient failures.
 *
 * 3 attempts provides a balance between recovering from brief outages
 * and avoiding excessive delays during persistent failures.
 * Total wait time with default backoff: ~7 seconds (1s + 2s + 4s)
 */
export const DEFAULT_MAX_RETRIES = 3;

/**
 * Default initial backoff delay in milliseconds.
 *
 * Used as the base for exponential backoff calculation:
 * - Attempt 1: 1000ms base
 * - Attempt 2: 2000ms base
 * - Attempt 3: 4000ms base
 *
 * Actual delays include jitter to prevent thundering herd problems.
 */
export const DEFAULT_INITIAL_BACKOFF_MS = 1000;

// =============================================================================
// Internal Types
// =============================================================================

/**
 * Options for the fetchWithRetry function.
 *
 * All fields are optional and fall back to DEFAULT_RETRY_CONFIG values.
 */
export interface FetchWithRetryOptions extends Partial<RetryConfig> {
  /**
   * Additional RequestInit options to pass to the fetch call.
   * The signal will be handled internally - do not pass a signal here.
   */
  fetchOptions?: Omit<RequestInit, 'signal'>;
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Calculates the delay for exponential backoff with jitter.
 *
 * The delay grows exponentially with each attempt to reduce load on
 * struggling servers. Jitter (randomness) is added to prevent multiple
 * clients from retrying simultaneously (thundering herd problem).
 *
 * Formula: `baseDelay * 2^attempt + random(0, jitterFactor * baseDelay * 2^attempt)`
 *
 * @param attempt - The current attempt number (0-indexed)
 * @param baseDelayMs - Base delay in milliseconds (default: 1000)
 * @param jitterFactor - Maximum jitter as a fraction of base delay (default: 0.3)
 * @returns The delay in milliseconds before the next retry
 *
 * @example
 * ```tsx
 * // Default configuration produces:
 * // Attempt 0: 1000-1300ms
 * // Attempt 1: 2000-2600ms
 * // Attempt 2: 4000-5200ms
 * const delay = calculateBackoffDelay(0); // ~1000-1300
 * const delay = calculateBackoffDelay(1); // ~2000-2600
 * const delay = calculateBackoffDelay(2); // ~4000-5200
 * ```
 */
export function calculateBackoffDelay(
  attempt: number,
  baseDelayMs: number = DEFAULT_INITIAL_BACKOFF_MS,
  jitterFactor: number = DEFAULT_RETRY_CONFIG.jitterFactor
): number {
  // Exponential backoff: 1s, 2s, 4s, etc.
  const baseDelay = baseDelayMs * Math.pow(2, attempt);
  // Add jitter to prevent thundering herd (up to jitterFactor% of base delay)
  const jitter = Math.random() * jitterFactor * baseDelay;
  return Math.floor(baseDelay + jitter);
}

/**
 * Creates a Promise that resolves after a specified delay.
 *
 * The delay can be cancelled via an AbortSignal, which causes the
 * Promise to reject with a DOMException (name: 'AbortError').
 *
 * This function properly cleans up the abort listener when the delay
 * completes naturally, preventing memory leaks.
 *
 * @param delayMs - The delay in milliseconds
 * @param signal - AbortSignal to allow cancellation of the delay
 * @returns Promise that resolves after the delay or rejects if aborted
 *
 * @throws {DOMException} With name 'AbortError' if the signal is aborted
 *
 * @example
 * ```tsx
 * const controller = new AbortController();
 *
 * // Wait 2 seconds, unless cancelled
 * try {
 *   await delay(2000, controller.signal);
 *   console.log('Delay completed');
 * } catch (error) {
 *   if (error instanceof DOMException && error.name === 'AbortError') {
 *     console.log('Delay was cancelled');
 *   }
 * }
 *
 * // Cancel from elsewhere
 * controller.abort();
 * ```
 */
export function delay(delayMs: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if already aborted before starting the delay
    if (signal.aborted) {
      reject(new DOMException('Delay aborted', 'AbortError'));
      return;
    }

    const timeoutId = setTimeout(() => {
      // Clean up the abort listener when delay completes normally
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, delayMs);

    // Handler for abort events
    const onAbort = () => {
      clearTimeout(timeoutId);
      reject(new DOMException('Delay aborted', 'AbortError'));
    };

    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Performs a fetch request with a configurable timeout.
 *
 * This function wraps the fetch call with an internal AbortController
 * to enforce a timeout. It also respects an external AbortSignal for
 * cleanup scenarios (e.g., component unmount).
 *
 * The function distinguishes between:
 * - Internal timeout: Throws BootstrapTimeoutError
 * - External abort: Re-throws the original AbortError
 *
 * @param url - The URL to fetch
 * @param signal - External AbortSignal for cleanup (component unmount)
 * @param timeoutMs - Timeout in milliseconds (default: 30000)
 * @param fetchOptions - Additional options to pass to fetch (excluding signal)
 * @returns The fetch Response
 *
 * @throws {BootstrapTimeoutError} If the request times out
 * @throws {DOMException} With name 'AbortError' if externally aborted
 * @throws {ApiError} If the API returns an error response
 *
 * @example
 * ```tsx
 * const controller = new AbortController();
 *
 * try {
 *   const response = await fetchWithTimeout('/api/data', controller.signal, 5000);
 *   const data = await response.json();
 * } catch (error) {
 *   if (error instanceof BootstrapTimeoutError) {
 *     console.log('Request timed out');
 *   }
 * }
 * ```
 */
export async function fetchWithTimeout(
  url: string,
  signal: AbortSignal,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
  fetchOptions?: Omit<RequestInit, 'signal'>
): Promise<Response> {
  // Check if already aborted before starting the fetch
  if (signal.aborted) {
    throw new DOMException('Fetch aborted before start', 'AbortError');
  }

  // Create an internal controller for timeout management
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs);

  // Handler to propagate external abort to our internal controller
  const onExternalAbort = () => timeoutController.abort();
  signal.addEventListener('abort', onExternalAbort, { once: true });

  try {
    const response = await api(url, {
      ...fetchOptions,
      signal: timeoutController.signal
    });
    return response;
  } catch (error) {
    // Determine if this was a timeout vs external abort
    if (error instanceof DOMException && error.name === 'AbortError') {
      // Check if it was external abort (component unmount or explicit cancellation)
      if (signal.aborted) {
        // Re-throw as-is for external abort
        throw error;
      }
      // This was our internal timeout - convert to a more descriptive error
      throw new BootstrapTimeoutError(timeoutMs / 1000);
    }
    // Re-throw other errors as-is
    throw error;
  } finally {
    // Always clean up: clear timeout and remove listener
    clearTimeout(timeoutId);
    signal.removeEventListener('abort', onExternalAbort);
  }
}

/**
 * Performs a fetch with automatic retry logic and exponential backoff.
 *
 * This is the main entry point for resilient fetch operations. It combines:
 * - Timeout handling (via fetchWithTimeout)
 * - Transient error detection (via isTransientError)
 * - Exponential backoff with jitter (via calculateBackoffDelay)
 * - Cancellable delays (via delay)
 *
 * The function respects the AbortSignal at all stages:
 * - Before starting each attempt
 * - During the fetch operation
 * - During backoff delays between retries
 *
 * Retry behavior:
 * - Network errors (TypeError): Retried
 * - Server errors (5xx): Retried
 * - Client errors (4xx): NOT retried (non-transient)
 * - Timeout errors: NOT retried (timeout is already generous)
 * - Abort errors: NOT retried (intentional cancellation)
 *
 * @typeParam T - The expected response type (parsed from JSON)
 * @param url - The URL to fetch
 * @param signal - AbortSignal for cancellation (pass from AbortController)
 * @param label - Descriptive label for logging purposes
 * @param options - Optional retry configuration and fetch options
 * @returns The parsed JSON response of type T
 *
 * @throws {RetryExhaustedError} If all retry attempts fail with transient errors
 * @throws {BootstrapTimeoutError} If a single request times out
 * @throws {ApiError} For non-retryable API errors (4xx, etc.)
 * @throws {DOMException} With name 'AbortError' if cancelled via signal
 *
 * @example
 * ```tsx
 * interface UserData {
 *   id: number;
 *   name: string;
 * }
 *
 * const controller = new AbortController();
 *
 * try {
 *   const data = await fetchWithRetry<UserData>(
 *     '/api/user/123',
 *     controller.signal,
 *     'user-fetch',
 *     { maxAttempts: 5, timeoutMs: 10000 }
 *   );
 *   console.log('User:', data.name);
 * } catch (error) {
 *   if (error instanceof RetryExhaustedError) {
 *     console.log(`Failed after ${error.attempts} attempts`);
 *   } else if (error instanceof BootstrapTimeoutError) {
 *     console.log('Request timed out');
 *   } else if (error instanceof DOMException && error.name === 'AbortError') {
 *     console.log('Request was cancelled');
 *   }
 * }
 *
 * // Cancel from component cleanup
 * useEffect(() => {
 *   return () => controller.abort();
 * }, []);
 * ```
 */
export async function fetchWithRetry<T>(
  url: string,
  signal: AbortSignal,
  label: string,
  options: FetchWithRetryOptions = {}
): Promise<T> {
  // Merge provided options with defaults
  const config: RetryConfig = {
    maxAttempts: options.maxAttempts ?? DEFAULT_RETRY_CONFIG.maxAttempts,
    baseDelayMs: options.baseDelayMs ?? DEFAULT_RETRY_CONFIG.baseDelayMs,
    timeoutMs: options.timeoutMs ?? DEFAULT_RETRY_CONFIG.timeoutMs,
    jitterFactor: options.jitterFactor ?? DEFAULT_RETRY_CONFIG.jitterFactor,
  };

  let lastError: Error = new Error(`Unknown error during ${label}`);

  for (let attempt = 0; attempt < config.maxAttempts; attempt++) {
    // Check if aborted before starting attempt
    if (signal.aborted) {
      throw new DOMException(`${label} aborted`, 'AbortError');
    }

    try {
      logger.info(
        { attempt: attempt + 1, maxAttempts: config.maxAttempts, url },
        `fetchWithRetry: Attempting ${label} fetch.`
      );

      const response = await fetchWithTimeout(
        url,
        signal,
        config.timeoutMs,
        options.fetchOptions
      );
      const data = await response.json();

      logger.info(
        { dataKeys: Object.keys(data as object), attempt: attempt + 1 },
        `fetchWithRetry: ${label} data received successfully.`
      );

      return data as T;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // If aborted (unmount or explicit cancellation), don't retry
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }

      // If it's a timeout error, don't retry (timeout is already generous)
      if (error instanceof BootstrapTimeoutError) {
        throw error;
      }

      // If it's a non-transient error, don't retry
      if (!isTransientError(error)) {
        logger.warn(
          { err: error, attempt: attempt + 1 },
          `fetchWithRetry: Non-transient error encountered during ${label}, not retrying.`
        );
        throw error;
      }

      // Calculate backoff delay
      const backoffMs = calculateBackoffDelay(attempt, config.baseDelayMs, config.jitterFactor);
      logger.warn(
        { err: error, attempt: attempt + 1, backoffMs },
        `fetchWithRetry: Transient error during ${label}, will retry after backoff.`
      );

      // Wait before retry (unless this was the last attempt)
      if (attempt < config.maxAttempts - 1) {
        try {
          await delay(backoffMs, signal);
        } catch {
          // Delay was aborted (component unmount or explicit cancellation)
          throw new DOMException(`${label} aborted during backoff`, 'AbortError');
        }
      }
    }
  }

  // All retries exhausted
  logger.error(
    { attempts: config.maxAttempts, lastError: lastError.message },
    `fetchWithRetry: All retry attempts exhausted for ${label}.`
  );
  throw new RetryExhaustedError(config.maxAttempts, lastError);
}
